import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/authStorage';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileAPI } from '../services/api';
import axios from 'axios';
import { HiOutlineSparkles, HiOutlineSearch, HiOutlineMail, HiOutlineUser, HiOutlineLightningBolt, HiOutlineLogout, HiOutlineSun, HiOutlineMoon, HiOutlineBell } from 'react-icons/hi';
import './Sidebar.css';

// Helper function to check if user has admin privileges
const isAdminUser = (role) => {
  return role === 'ADMIN' || role === 'SUPER_ADMIN' || role === 'MODERATOR';
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [profile, setProfile] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Track viewport so we can hide the night-mode toggle in the mobile bottom nav.
  // On mobile, night mode lives in Profile > Settings instead.
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadProfile();
    loadUnreadCount();
    loadNotificationCount();
    
    // Reload profile and unread count every 30 seconds
    const interval = setInterval(() => {
      loadProfile();
      loadUnreadCount();
      loadNotificationCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Load pending counts for admins
    if (isAdminUser(profile?.role)) {
      loadPendingCounts();
      
      // Reload pending counts every 30 seconds
      const interval = setInterval(() => {
        loadPendingCounts();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [profile]);

  // Reload profile when location changes (e.g., after updating profile picture)
  useEffect(() => {
    loadProfile();
  }, [location.pathname]);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getMyProfile();
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const { conversationAPI } = require('../services/api');
      const response = await conversationAPI.getUnreadCount();
      setUnreadCount(response.data || 0);
    } catch (err) {
      console.error('Failed to load unread count:', err);
      setUnreadCount(0);
    }
  };
  
  const loadNotificationCount = async () => {
    // Only load notifications for female users and admins
    if (!profile || (profile.gender !== 'FEMALE' && profile.user?.role !== 'ADMIN')) {
      setNotificationCount(0);
      return;
    }
    
    try {
      const { creatorAPI } = require('../services/api');
      const response = await creatorAPI.getUnreadCount();
      setNotificationCount(response.data.count || 0);
    } catch (err) {
      // Silently fail - user might not be a creator
      console.error('Failed to load notification count:', err);
      setNotificationCount(0);
    }
  };

  const loadPendingCounts = async () => {
    try {
      const token = getToken();
      
      // Load pending approvals
      const approvalsResponse = await axios.get(`${API_URL}/admin/pending-approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Load pending verification requests
      const verificationsResponse = await axios.get(`${API_URL}/verification/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const approvalsCount = Array.isArray(approvalsResponse.data) ? approvalsResponse.data.length : 0;
      const verificationsCount = Array.isArray(verificationsResponse.data) ? verificationsResponse.data.length : 0;
      
      setPendingCount(approvalsCount + verificationsCount);
    } catch (err) {
      console.error('Failed to load pending counts:', err);
      setPendingCount(0);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/profile') {
      return location.pathname === '/profile';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className={`sidebar ${darkMode ? 'dark-mode' : ''}`}>
      <div className="sidebar-header">
        <h2 className="sidebar-logo">KwikLuv</h2>
        <p className="sidebar-tagline">No Strings Attached</p>
      </div>

      {/* User Profile Section */}
      {profile && (
        <Link to="/profile" className="sidebar-user-profile">
          <div className="sidebar-user-avatar">
            {profile.photoUrls && profile.photoUrls.length > 0 ? (
              <img src={profile.photoUrls[0]} alt={profile.name} />
            ) : (
              <div className="sidebar-avatar-placeholder">
                {getInitials(profile.name)}
              </div>
            )}
          </div>
          <div className="sidebar-user-info">
            <h3>{profile.name}</h3>
            <p>{profile.location || 'Set your location'}</p>
          </div>
        </Link>
      )}

      <nav className="sidebar-nav">
        <Link 
          to="/feed" 
          className={`sidebar-link ${isActive('/feed') ? 'active' : ''}`}
        >
          <HiOutlineSparkles className="sidebar-icon" />
          <span className="sidebar-text">Feed</span>
        </Link>

        <Link 
          to="/discover" 
          className={`sidebar-link ${isActive('/discover') ? 'active' : ''}`}
        >
          <HiOutlineSearch className="sidebar-icon" />
          <span className="sidebar-text">Browse</span>
        </Link>

        <Link 
          to="/inbox" 
          className={`sidebar-link ${isActive('/inbox') ? 'active' : ''}`}
          style={{ position: 'relative' }}
        >
          <HiOutlineMail className="sidebar-icon" />
          <span className="sidebar-text">Inbox</span>
          {unreadCount > 0 && (
            <span className="notification-badge" style={{
              position: 'absolute',
              top: '8px',
              right: '12px',
              background: '#ff1493',
              color: 'white',
              fontSize: '11px',
              fontWeight: '800',
              padding: '3px 8px',
              borderRadius: '12px',
              minWidth: '20px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(255, 20, 147, 0.4)',
              animation: 'pulse 2s infinite'
            }}>
              {unreadCount}
            </span>
          )}
        </Link>

        {/* Notifications - Only for female users (creators) and admins */}
        {profile && (profile.gender === 'FEMALE' || profile.user?.role === 'ADMIN') && (
          <Link 
            to="/notifications" 
            className={`sidebar-link ${isActive('/notifications') ? 'active' : ''}`}
            style={{ position: 'relative' }}
          >
            <HiOutlineBell className="sidebar-icon" />
            <span className="sidebar-text">Notifications</span>
            {notificationCount > 0 && (
              <span className="notification-badge" style={{
                position: 'absolute',
                top: '8px',
                right: '12px',
                background: '#1877f2',
                color: 'white',
                fontSize: '11px',
                fontWeight: '800',
                padding: '3px 8px',
                borderRadius: '12px',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {notificationCount}
              </span>
            )}
          </Link>
        )}

        <Link 
          to="/profile" 
          className={`sidebar-link ${isActive('/profile') ? 'active' : ''}`}
        >
          <HiOutlineUser className="sidebar-icon" />
          <span className="sidebar-text">My Profile</span>
        </Link>

        {/* Early Payout link removed - creator mode and payouts disabled in UI */}

        {/* Verification link removed - verification handled by admin, on hold for now */}

        {/* Admin Panel Link - Only for admins */}
        {isAdminUser(profile?.role) && (
          <Link 
            to="/admin" 
            className={`sidebar-link ${isActive('/admin') ? 'active' : ''}`}
            style={{
              background: '#fbbf24',
              color: '#1f2937',
              marginTop: '8px',
              fontWeight: 'bold',
              position: 'relative'
            }}
          >
            <HiOutlineLightningBolt className="sidebar-icon" style={{ color: '#1f2937' }} />
            <span className="sidebar-text" style={{ color: '#1f2937' }}>Admin Panel</span>
            {pendingCount > 0 && (
              <span className="notification-badge" style={{
                position: 'absolute',
                top: '8px',
                right: '12px',
                background: '#ef4444',
                color: 'white',
                fontSize: '11px',
                fontWeight: '800',
                padding: '3px 8px',
                borderRadius: '12px',
                minWidth: '20px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
                animation: 'pulse 2s infinite'
              }}>
                {pendingCount}
              </span>
            )}
          </Link>
        )}
      </nav>

      <div className="sidebar-footer">
        {/* Night mode toggle. On desktop it's a full row with a switch; on mobile it
            collapses to a compact icon + short label so every item still fits the bottom nav. */}
        <button 
          className="sidebar-theme-toggle"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? (
            <HiOutlineSun className="sidebar-icon" />
          ) : (
            <HiOutlineMoon className="sidebar-icon" />
          )}
          <span className="sidebar-text">
            {isMobile ? 'Theme' : (darkMode ? 'Dark Mode' : 'Light Mode')}
          </span>
          <label className="sidebar-toggle-switch" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={darkMode}
              onChange={() => setDarkMode(!darkMode)}
            />
            <span className="sidebar-toggle-slider"></span>
          </label>
        </button>

        <button 
          onClick={handleLogout} 
          className="sidebar-link sidebar-logout"
        >
          <HiOutlineLogout className="sidebar-icon" />
          <span className="sidebar-text">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
